import type { CSSProperties, JSX, ReactNode } from 'react';
import type { StatusKind } from '@/lib/bands';
import { meterKind } from '@/lib/bands';
import { clamp } from '@/lib/math';

const FILL_COLOR: Record<StatusKind, string> = {
  good: 'var(--st-good-bd)',
  warn: 'var(--st-warn-bd)',
  bad: 'var(--st-bad-bd)',
  info: 'var(--pnet-blue)',
  neutral: 'var(--pnet-blue)',
};

export function Meter({
  value,
  kind,
  small = false,
  ariaLabel,
}: {
  readonly value: number;
  readonly kind?: StatusKind | undefined;
  readonly small?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
}): JSX.Element {
  const pct = clamp(value, 0, 100);
  return (
    <div
      className={`meter ${small ? 'meter--sm' : ''}`}
      role="meter"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel ?? 'Progress'}
    >
      <span style={{ width: `${pct}%`, background: FILL_COLOR[kind ?? 'neutral'] }} />
    </div>
  );
}

/** Meter whose colour is chosen from the value, using the shared ladder. */
export function AutoMeter({
  value,
  ariaLabel,
}: {
  readonly value: number;
  readonly ariaLabel?: string | undefined;
}): JSX.Element {
  const kind = meterKind(value);
  return (
    <Meter
      value={value}
      {...(kind === undefined ? {} : { kind })}
      {...(ariaLabel === undefined ? {} : { ariaLabel })}
    />
  );
}

export function Stat({
  label,
  value,
  sub,
  valueStyle,
  children,
}: {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly sub?: ReactNode | undefined;
  readonly valueStyle?: CSSProperties | undefined;
  readonly children?: ReactNode | undefined;
}): JSX.Element {
  return (
    <div className="stat">
      <span className="lbl">{label}</span>
      <span className="val" style={valueStyle}>
        {value}
      </span>
      {sub !== undefined && <span className="sub">{sub}</span>}
      {children}
    </div>
  );
}

export function KeyValue({
  label,
  value,
  labelStyle,
  valueStyle,
}: {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly labelStyle?: CSSProperties | undefined;
  readonly valueStyle?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <div className="kv">
      <span className="muted" style={labelStyle}>
        {label}
      </span>
      <b style={valueStyle}>{value}</b>
    </div>
  );
}

/** Small uppercase label with a value beneath, for tile rows inside cards. */
export function MicroStat({
  label,
  value,
}: {
  readonly label: ReactNode;
  readonly value: ReactNode;
}): JSX.Element {
  return (
    <div>
      <div className="eyebrow" style={{ letterSpacing: '.06em' }}>
        {label}
      </div>
      <div className="body-sm" style={{ fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}
